import { useState } from 'react'

import { formatClock } from '@krp/client-runtime'
import { Button } from '@krp/ui-kit'

import { terminalCards, type TerminalCardVm } from '../model/terminals'
import type { EquipmentSnapshot, HardwareActions, HardwareLinks, HardwareTabId } from '../model/types'
import { Mark } from './Mark'
import { blockReason, type TabProps } from './tabSupport'
import { ScanPanel } from './ScanPanel'

const LINK_TONE = { unchecked: 'wait', ok: 'ok', failed: 'block' } as const

function TerminalCard({ card, actions, locked, busy }: TabProps & { card: TerminalCardVm; actions: HardwareActions }) {
  const [confirming, setConfirming] = useState(false)
  const reason = blockReason(locked, busy)
  return (
    <li className={`hw-terminal hw-box hw-box--${LINK_TONE[card.linkState]}`} aria-label={card.title}>
      <h3 className="hw-title">
        {card.title}
        {card.details.length > 0 && <> · {card.details.join(' · ')}</>}
        {card.primary && <> · основной</>}
      </h3>
      <p>
        <Mark glyph={card.linkGlyph} word={card.linkWord} tone={LINK_TONE[card.linkState]} />
        {card.checkedAt !== null && <span className="hw-hint"> (проверено {formatClock(card.checkedAt)})</span>}
      </p>
      <p className="hw-hint">{card.linkText}</p>
      <p>Умеет: {card.abilities}</p>
      {card.capabilityNote !== null && <p className="hw-hint">{card.capabilityNote}</p>}
      {confirming && (
        <p role="alert" className="hw-confirm">
          Оплата картой на этом компьютере перестанет работать. Удалить терминал?
        </p>
      )}
      <div className="hw-buttons">
        <Button
          variant="secondary"
          actionId="platform.hardware.terminal-test"
          disabled={reason !== null}
          disabledReason={reason}
          onClick={() => actions.testTerminal(card.id)}
        >
          Проверить связь
        </Button>
        {!card.primary && (
          <Button
            variant="secondary"
            actionId="platform.hardware.terminal-activate"
            disabled={reason !== null || !card.canMakePrimary}
            disabledReason={reason ?? (card.canMakePrimary ? null : 'Уже основной')}
            onClick={() => actions.activateTerminal(card.id)}
          >
            Сделать основным
          </Button>
        )}
        {!confirming ? (
          <Button
            variant="danger"
            actionId="platform.hardware.terminal-remove"
            disabled={reason !== null}
            disabledReason={reason}
            onClick={() => setConfirming(true)}
          >
            Удалить
          </Button>
        ) : (
          <>
            <Button
              variant="danger"
              actionId="platform.hardware.terminal-remove"
              disabled={reason !== null}
              disabledReason={reason}
              onClick={() => {
                setConfirming(false)
                actions.removeTerminal(card.id)
              }}
            >
              Да, удалить
            </Button>
            <Button variant="ghost" actionId="platform.hardware.confirm-cancel" onClick={() => setConfirming(false)}>
              Не надо
            </Button>
          </>
        )}
      </div>
    </li>
  )
}

/**
 * Вкладка «Терминалы» (п. 2.2, 3.5): сканирование, найденные с формой недостающих полей, сохранённые терминалы со статусом связи.
 * «Связь не проверялась» — отдельное слово, не «готов». Проверка связи — только по кнопке.
 */
export function TerminalsTab({
  snapshot,
  links,
  actions,
  locked,
  busy,
  available,
  onOpenTab,
}: TabProps & {
  snapshot: EquipmentSnapshot
  links: HardwareLinks
  actions: HardwareActions
  available: boolean
  onOpenTab: (tab: HardwareTabId) => void
}) {
  if (!available) {
    return (
      <section aria-label="Терминалы" className="hw-tab">
        <p className="hw-hint">Недоступно, пока нет связи с программой-драйвером: терминал подключается через неё.</p>
      </section>
    )
  }
  const cards = terminalCards(snapshot)
  return (
    <section aria-label="Терминалы" className="hw-tab">
      {snapshot.terminals === null && <p className="hw-hint">Не удалось получить список терминалов. Нажмите «Проверить сейчас».</p>}
      <ScanPanel snapshot={snapshot} category="payment-terminal" links={links} actions={actions} locked={locked} busy={busy} onOpenTab={onOpenTab} />
      {cards.length > 0 && (
        <ul className="hw-candidates" aria-label="Подключённые терминалы">
          {cards.map((card) => (
            <TerminalCard key={card.id} card={card} actions={actions} locked={locked} busy={busy} />
          ))}
        </ul>
      )}
    </section>
  )
}
